import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import UmlShapeWidgetBase from './UmlShapeWidgetBase';
import { JxTooltip, JxTooltipRowItemDescription, JxHint } from '@jx';
import { UML_ICON_SHAPE_ICONS, getIconInfo } from '../../Types/UmlIconShapeIcons.js';
import './UmlIconWidget.css';

// Pure painter for UmlIconShape: icon (click -> picker menu) + caption + stereotype.

class UmlIconWidget extends UmlShapeWidgetBase {
    state = { ...this.state, menuAnchorEl: null };

    getMinSize() {
        const shape = this.props.shape;
        return {
            // Smaller default (2026-07-12) — was visually much heavier than a Class widget's
            // collapsed header (umlConst.CLASS_COLLAPSED_HEADER_HEIGHT=34, see UmlConsts.js); this now
            // lands close to that.
            width: Math.max(90, shape.caption.text.length * 7 + 34),
            height: 40,
        };
    }

    getEditableZoneClassName() {
        return 'uml-icon-widget-name';
    }

    getEditableInputClassName() {
        return 'uml-icon-widget-name-input';
    }

    getEditableText() {
        return this.props.shape.caption.text;
    }

    // Icon click opens the picker menu instead of starting a shape-drag —
    // stopPropagation so handleDragMouseDown (bound on the outer div) never fires.
    handleIconClick = (e) => {
        e.stopPropagation();
        this.setState({ menuAnchorEl: e.currentTarget });
    };

    handleMenuClose = () => {
        this.setState({ menuAnchorEl: null });
    };

    handleIconKindSelected = (iconKind) => {
        this.props.onShapeIconKindChanged?.(this.props.shape.id, iconKind);
        this.setState({ menuAnchorEl: null });
    };

    // MUI's own outside-click/backdrop detection didn't reliably close the menu in this app
    // (2026-07-12) — too much custom pointer/drag handling elsewhere for it to behave
    // predictably. Rolled our own instead: a capture-phase document listener, active only
    // while the menu is open, that closes it unless the click landed inside the menu's Paper
    // (tracked via paperRef, set through slotProps.paper.ref below). Capture phase runs BEFORE
    // the Paper's own onMouseDown stopPropagation (that one only stops BUBBLE-phase
    // propagation to the shape's drag handler, doesn't affect this).
    paperRef = null;

    handleDocumentMouseDown = (e) => {
        if (this.paperRef && this.paperRef.contains(e.target)) {
            return; // click landed inside the menu itself — let MenuItem's own onClick handle it
        }
        this.handleMenuClose();
    };

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate?.(prevProps, prevState);
        const wasOpen = Boolean(prevState.menuAnchorEl);
        const isOpen = Boolean(this.state.menuAnchorEl);
        if (isOpen && !wasOpen) {
            document.addEventListener('mousedown', this.handleDocumentMouseDown, true);
        } else if (!isOpen && wasOpen) {
            document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount?.();
        document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
    }

    render() {
        const shape = this.props.shape;
        const iconInfo = getIconInfo(shape.iconKind);
        const { Icon } = iconInfo;
        // Fallback stereotype (2026-07-12) — auto-derived from the icon kind (e.g. iconKind
        // 'database' -> "<<database>>") when the user hasn't typed their own. Purely a display
        // fallback, doesn't touch caption.stereotype itself — a real typed value always wins,
        // and switching iconKind again just changes what the fallback WOULD show. 'none' has
        // no sensible auto-label, so no fallback there (stays blank unless typed).
        const stereotypeText = shape.caption.stereotype || (shape.iconKind !== 'none' ? iconInfo.label.toLowerCase() : null);
        const captionZone = shape.description ? (
            <JxHint title={shape.description}>{this.renderEditableZone(shape.caption.text)}</JxHint>
        ) : (
            this.renderEditableZone(shape.caption.text)
        );
        return (
            <JxTooltip followCursor title={<JxTooltipRowItemDescription label="Press D" value="Delete shape" />}>
                <div
                    className={`uml-icon-widget${this.props.isSelected ? ' uml-selected' : ''}`}
                    data-shape-id={shape.id}
                    style={{
                        left: shape.x,
                        top: shape.y,
                        width: shape.w,
                        height: shape.h,
                        zIndex: this.props.stackIndex ?? shape.zIndex,
                    }}
                    onMouseDown={this.handleDragMouseDown}
                >
                    <JxTooltip title="Click to change icon">
                        <div className="uml-icon-widget-icon-zone" onMouseDown={this.handleIconClick}>
                            {Icon ? <Icon sx={{ fontSize: 16 }} /> : <div className="uml-icon-widget-icon-placeholder" />}
                        </div>
                    </JxTooltip>

                    <div className="uml-icon-widget-caption">
                        {captionZone}
                        {stereotypeText && <div className="uml-icon-widget-stereotype">&lt;&lt;{stereotypeText}&gt;&gt;</div>}
                    </div>

                    {this.renderResizeHandles()}

                    <Menu
                        anchorEl={this.state.menuAnchorEl}
                        open={Boolean(this.state.menuAnchorEl)}
                        onClose={this.handleMenuClose}
                        slotProps={{
                            paper: {
                                ref: (el) => {
                                    this.paperRef = el;
                                },
                                // stopPropagation nodig ondanks de portal (2026-07-12): synthetic events
                                // bubbelen via de REACT-tree, niet de DOM-tree, dus een mousedown op een
                                // MenuItem hierbinnen bereikt anders alsnog handleDragMouseDown op de
                                // buitenste shape-div (React-ouder van dit <Menu>), wat een shape-drag
                                // start en de klik effectief negeert ("klikken doet niks"-bug).
                                onMouseDown: (e) => e.stopPropagation(),
                                sx: {
                                    background: '#1c2b3f',
                                    border: '1px solid #3a4a63',
                                    color: '#e8edf5',
                                },
                            },
                        }}
                    >
                        {Object.entries(UML_ICON_SHAPE_ICONS).map(([key, { label, Icon: MenuIcon }]) => (
                            <MenuItem
                                key={key}
                                selected={key === shape.iconKind}
                                onClick={() => this.handleIconKindSelected(key)}
                                sx={{
                                    fontSize: 13,
                                    color: '#e8edf5',
                                    gap: '10px',
                                    '&:hover': { background: 'rgba(102, 178, 255, 0.15)' },
                                    '&.Mui-selected': { background: 'rgba(102, 178, 255, 0.2)' },
                                    '&.Mui-selected:hover': { background: 'rgba(102, 178, 255, 0.28)' },
                                }}
                            >
                                <span className="uml-icon-menu-item-icon">{MenuIcon ? <MenuIcon sx={{ fontSize: 18 }} /> : null}</span>
                                <span className="uml-icon-menu-item-label">{label}</span>
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            </JxTooltip>
        );
    }
}

export default UmlIconWidget;
